import { useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

export default function AboutPage() {
  const { t } = useTranslation();

  const principles = [
    { icon: 'verified', title: t('aboutPage.principles.integrity.title'), desc: t('aboutPage.principles.integrity.desc') },
    { icon: 'gavel', title: t('aboutPage.principles.compliance.title'), desc: t('aboutPage.principles.compliance.desc') },
    { icon: 'lock', title: t('aboutPage.principles.confidentiality.title'), desc: t('aboutPage.principles.confidentiality.desc') },
    { icon: 'visibility', title: t('aboutPage.principles.transparency.title'), desc: t('aboutPage.principles.transparency.desc') },
    { icon: 'handshake', title: t('aboutPage.principles.commitment.title'), desc: t('aboutPage.principles.commitment.desc') },
  ];

  useEffect(() => {
    document.title = t('aboutPage.pageTitle');
  }, [t]);

  return (
    <main id="main-content">
      <section className="pt-40 pb-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center_top,rgba(168,67,151,0.08),transparent_70%)] pointer-events-none" />
        <img src="/logo-icon.png" alt="" aria-hidden="true" className="absolute -top-20 -right-20 w-[450px] h-[450px] object-contain opacity-[0.04] rotate-[-20deg] pointer-events-none select-none" />
        <div className="mx-auto min-w-0 max-w-4xl px-4 sm:px-6">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black font-headline tracking-tight">
              {t('aboutPage.heroTitle')} <span className="gradient-text">{t('aboutPage.heroTitleHighlight')}</span>
            </h1>
            <p className="text-on-surface-variant text-lg mt-4">{t('aboutPage.heroBadge')}</p>
            <p className="text-on-surface-variant/70 mt-6 max-w-2xl mx-auto leading-relaxed">
              {t('aboutPage.heroDescription')}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-surface-container/20">
        <div className="mx-auto grid min-w-0 max-w-7xl grid-cols-1 gap-12 px-4 sm:px-6 md:grid-cols-2">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <h2 className="text-3xl font-black font-headline mb-4">{t('aboutPage.mission.title')}</h2>
            <p className="text-on-surface-variant leading-relaxed">
              {t('aboutPage.mission.text')}
            </p>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }}>
            <h2 className="text-3xl font-black font-headline mb-4">{t('aboutPage.vision.title')}</h2>
            <p className="text-on-surface-variant leading-relaxed">
              {t('aboutPage.vision.text')}
            </p>
            <div className="mt-6 flex gap-4">
              <div className="text-center">
                <p className="text-3xl font-black gradient-text">{t('aboutPage.vision.stats.years.value')}</p>
                <p className="text-xs text-on-surface-variant uppercase tracking-widest">{t('aboutPage.vision.stats.years.label')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black gradient-text">{t('aboutPage.vision.stats.companies.value')}</p>
                <p className="text-xs text-on-surface-variant uppercase tracking-widest">{t('aboutPage.vision.stats.companies.label')}</p>
              </div>
              <div className="text-center">
                <p className="text-3xl font-black gradient-text">{t('aboutPage.vision.stats.coverage.value')}</p>
                <p className="text-xs text-on-surface-variant uppercase tracking-widest">{t('aboutPage.vision.stats.coverage.label')}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="py-16 bg-[#1a1a1a]">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <h2 className="text-3xl font-black font-headline text-center mb-10">
            {t('aboutPage.pillars.title')} <span className="gradient-text">{t('aboutPage.pillars.titleHighlight')}</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: 'hub', title: t('aboutPage.pillars.solution.title'), desc: t('aboutPage.pillars.solution.desc') },
              { icon: 'lightbulb', title: t('aboutPage.pillars.innovation.title'), desc: t('aboutPage.pillars.innovation.desc') },
              { icon: 'shield', title: t('aboutPage.pillars.security.title'), desc: t('aboutPage.pillars.security.desc') },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.title}
                className="text-center p-8 border border-purple-primary/20 rounded-xl bg-[#222222]"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.1 }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                whileHover={{ y: -4, borderColor: "rgba(168,67,151,0.5)" }}
              >
                <div className="w-16 h-16 rounded-xl bg-purple-primary/10 border border-purple-primary/30 flex items-center justify-center mx-auto mb-5">
                  <span className="material-symbols-outlined text-accent-magenta text-[32px]">{pillar.icon}</span>
                </div>
                <h3 className="text-xl font-bold font-headline mb-2 gradient-text">{pillar.title}</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <h2 className="text-4xl font-black font-headline text-center mb-12">
            {t('aboutPage.principles.title')} <span className="gradient-text">{t('aboutPage.principles.titleHighlight')}</span>
          </h2>
          {/* First row: 3 cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            {principles.slice(0, 3).map((v, i) => (
              <motion.div key={v.title} className="p-6 border border-outline-variant/30 rounded-xl hover:border-purple-primary/50 transition-all duration-300" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: i * 0.08, duration: 0.5 }}>
                <span className="material-symbols-outlined text-accent-magenta text-[28px] mb-3 block">{v.icon}</span>
                <h3 className="text-lg font-bold font-headline mb-2">{v.title}</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
          {/* Second row: 2 cards centered */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
            {principles.slice(3).map((v, i) => (
              <motion.div key={v.title} className="p-6 border border-outline-variant/30 rounded-xl hover:border-purple-primary/50 transition-all duration-300" initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.1 }} transition={{ delay: (i + 3) * 0.08, duration: 0.5 }}>
                <span className="material-symbols-outlined text-accent-magenta text-[28px] mb-3 block">{v.icon}</span>
                <h3 className="text-lg font-bold font-headline mb-2">{v.title}</h3>
                <p className="text-sm text-on-surface-variant leading-relaxed">{v.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto min-w-0 max-w-7xl px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between p-8 bg-surface-container border border-outline-variant/30 rounded-2xl">
            <h3 className="text-xl md:text-2xl font-bold font-headline mb-4 md:mb-0">
              {t('aboutPage.cta.title')} <span className="gradient-text">{t('aboutPage.cta.titleHighlight')}</span>
            </h3>
            <Link to="/contact" className="px-8 py-3 bg-gradient-to-r from-purple-primary to-accent-magenta text-white rounded-xl font-bold font-headline hover:scale-105 active:scale-95 transition-transform">
              {t('aboutPage.cta.button')}
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}
